import { useCallback, useEffect } from "react";
import { useMap } from "react-map-gl/maplibre";

import { caluclateOverlap } from "./dem-utils/calculateOverlap";
import { TwoDWaterflowProps } from "./two-d-waterflow";
import { BasicInfo, Bounds, WindData, WindowWind } from "./types";

interface UseRedrawCanvasHooksProps extends TwoDWaterflowProps {
  getCanvasses: () => {
    canvas1: HTMLCanvasElement | null;
    canvas2: HTMLCanvasElement | null;
  };
  basicInfo: BasicInfo;
  windData: WindData | null;
  hideCanvasses: boolean;
  activeCanvasFlipped: boolean;
  setActiveCanvasFlipped: (flip: boolean) => void;
  savedBounds: Bounds | null;
  setSavedBounds: (bounds: Bounds) => void;
  setAnimateBoth: (both: boolean) => void;
}

let savedFlipCanvas = false;
let timeoutId: NodeJS.Timeout | null = null;

const EMPTY_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==";
const emptyImage = new Image();
emptyImage.src = EMPTY_IMAGE;

// Here is where we actually redraw and flip the canvasses
export const useRedrawCanvasHooks = ({
  terrainCanvasInfo,
  getCanvasses,
  basicInfo,
  hideCanvasses,
  windData,
  activeCanvasFlipped,
  setActiveCanvasFlipped,
  savedBounds,
  setSavedBounds,
  setAnimateBoth,
}: UseRedrawCanvasHooksProps) => {
  const map = useMap().current;

  // Resize canvas to found basic info
  const redrawCanvas = useCallback(
    (flipCanvas?: boolean) => {
      const actualyRedraw = (flipCanvas?: boolean) => {
        if (!basicInfo || !map || hideCanvasses || !windData) return;
        const { bounds, width, height } = basicInfo;

        const mapBounds = map.getBounds();
        const nw = mapBounds.getNorthWest();
        const leftTop = [nw.lng, nw.lat];
        const se = mapBounds.getSouthEast();
        const rightBottom = [se.lng, se.lat];

        // Get and apply new bounds or return
        const newBounds: Bounds = [
          Math.max(bounds[0], leftTop[0]),
          Math.min(bounds[1], leftTop[1]),
          Math.min(bounds[2], rightBottom[0]),
          Math.max(bounds[3], rightBottom[1]),
        ];

        const { canvas1, canvas2 } = getCanvasses();
        if (!canvas1 || !canvas2) return;

        // Calculate if only part of canvas is in bounds
        const totalDistanceX = bounds[2] - bounds[0];
        const totalDistanceY = bounds[1] - bounds[3];
        const distanceLeft = leftTop[0] - bounds[0];
        const distanceTop = bounds[1] - leftTop[1];
        const distanceRight = rightBottom[0] - bounds[2];
        const distanceBottom = bounds[3] - rightBottom[1];

        const xPoint = Math.max((distanceLeft * width) / totalDistanceX, 0);
        const yPoint = Math.max((distanceTop * height) / totalDistanceY, 0);
        const xPointEnd = Math.min((distanceRight * width) / totalDistanceX, 0);
        const yPointEnd = Math.min((distanceBottom * height) / totalDistanceY, 0);

        let activeCanvasFlippedNow = activeCanvasFlipped;
        // Switch canvas if viewport moved
        if (flipCanvas) {
          activeCanvasFlippedNow = !activeCanvasFlipped;
        }

        const windDataImage =
          caluclateOverlap({
            width,
            height,
            bounds,
            terrainCanvasInfo,
            windImage: windData.image,
          }) || windData.image;

        // USED FOR TESTING
        // const testCanvas = document.getElementById("testing") as any;
        // testCanvas.width = width;
        // testCanvas.height = height;
        // testCanvas.style.width = width / 4 + "px";
        // testCanvas.style.height = height / 4 + "px";
        // const testCtx = testCanvas.getContext("2d") as unknown as CanvasRenderingContext2D;
        // testCtx.drawImage(windDataImage as CanvasImageSource, 0, 0);

        // Draw new image to offscreen canvas and aplly it
        const offscreen = new OffscreenCanvas(width, height);
        const ctx = offscreen.getContext("2d") as unknown as CanvasRenderingContext2D;

        const windWidth = xPointEnd + width - xPoint;
        const windHeight = yPointEnd + height - yPoint;

        ctx.drawImage(
          windDataImage as CanvasImageSource,
          xPoint,
          yPoint,
          windWidth,
          windHeight,
          0,
          0,
          width,
          height
        );
        const newWindData = { ...windData, width: windWidth, height: windHeight };

        const windowWind = window as WindowWind;
        const activeWind = activeCanvasFlippedNow ? windowWind.wind1 : windowWind.wind2;
        activeWind && activeWind.setWind({ ...newWindData, image: offscreen });

        // Switch which canvas is visible if viewport moved
        if (flipCanvas || !savedBounds) {
          const nonActiveWind = !activeCanvasFlippedNow ? windowWind.wind1 : windowWind.wind2;
          nonActiveWind && nonActiveWind.setWind({ ...newWindData, image: emptyImage });

          setActiveCanvasFlipped(activeCanvasFlippedNow);
          setSavedBounds(newBounds);
        }
      };
      // Prevents redrawing 7 times in the span of 1 second
      if (flipCanvas) savedFlipCanvas = true;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        actualyRedraw(savedFlipCanvas);

        setAnimateBoth(false);
        savedFlipCanvas = false;
      }, 1000);
    },
    [
      activeCanvasFlipped,
      getCanvasses,
      map,
      savedBounds,
      terrainCanvasInfo,
      hideCanvasses,
      windData,
    ]
  );

  // Redraw the image if twoDFlowData is updated
  useEffect(() => redrawCanvas(), [windData]);
  // Redraw completly if the terrain updates
  useEffect(() => redrawCanvas(true), [terrainCanvasInfo, hideCanvasses]);

  // Position canvas and redraw if user stopped moving
  useEffect(() => {
    const setAnimateBothTrue = () => setAnimateBoth(true);
    const startRedrawCanvas = () => redrawCanvas(true);

    map?.on("movestart", setAnimateBothTrue);
    map?.on("moveend", startRedrawCanvas);
    return () => {
      map?.off("movestart", setAnimateBothTrue);
      map?.off("moveend", startRedrawCanvas);
    };
  }, [map, redrawCanvas]);

  return { redrawCanvas };
};
